import { useState, useEffect } from "react";
import { Loader2, Search, DollarSign, Calendar, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { quoteJobService } from "../../../services/quoteJobService";
import MarkAsPaidModal from "./MarkAsPaidModal";

export default function PaymentsPage() {
    const [loading, setLoading] = useState(true);
    const [quotes, setQuotes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchQuotes();
    }, []);

    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const response = await quoteJobService.getQuotesWithPayments();
            if (response.data.success) {
                setQuotes(response.data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch payments", error);
            toast.error("Failed to load payments data");
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsPaid = (quote) => {
        setSelectedQuote(quote);
        setIsModalOpen(true);
    };

    const handlePaymentSuccess = () => {
        fetchQuotes();
        setIsModalOpen(false);
        setSelectedQuote(null);
    };

    const filteredQuotes = quotes.filter(quote =>
        quote.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ("Quote #" + quote.id).toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <section className="p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
                <p className="text-sm text-gray-600 mt-1">Manage quote payments and history</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-5 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by client name or quote number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">Quote Details</th>
                                <th className="px-6 py-4">Client</th>
                                <th className="px-6 py-4 text-right">Total Amount</th>
                                <th className="px-6 py-4 text-right">Paid Amount</th>
                                <th className="px-6 py-4 text-right">Balance</th>
                                <th className="px-6 py-4 text-center">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredQuotes.length > 0 ? (
                                filteredQuotes.map((quote) => (
                                    <tr key={quote.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">
                                                {quote.quote_number || `Quote #${quote.id}`}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(quote.created_at).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">
                                                {quote.first_name} {quote.last_name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {quote.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right font-medium">
                                            ${(
                                                parseFloat(quote.amount || 0) +
                                                parseFloat(quote.report_generation_fees || 0) +
                                                parseFloat(quote.quote_fuel || 0)
                                            ).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-right text-green-600">
                                            ${quote.is_paid ? (
                                                parseFloat(quote.amount || 0) +
                                                parseFloat(quote.report_generation_fees || 0) +
                                                parseFloat(quote.quote_fuel || 0)
                                            ).toFixed(2) : '0.00'}
                                        </td>
                                        <td className="px-6 py-4 text-right text-red-600 font-medium">
                                            ${quote.is_paid ? '0.00' : (
                                                parseFloat(quote.amount || 0) +
                                                parseFloat(quote.report_generation_fees || 0) +
                                                parseFloat(quote.quote_fuel || 0)
                                            ).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${quote.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {quote.is_paid ? 'Paid' : 'Unpaid'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {!quote.is_paid && (
                                                <button
                                                    onClick={() => handleMarkAsPaid(quote)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-xs font-medium transition-colors"
                                                >
                                                    <DollarSign className="w-3.5 h-3.5" />
                                                    Mark as Paid
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <AlertCircle className="w-10 h-10 text-gray-300 mb-3" />
                                            <p className="font-medium">No payments found</p>
                                            <p className="text-xs mt-1">No quotes available with payment information.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && selectedQuote && (
                <MarkAsPaidModal
                    quote={selectedQuote}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={handlePaymentSuccess}
                />
            )}
        </section>
    );
}
